import React, {useEffect, useState} from 'react';
import './AdminUsersPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import {API_BASE} from '../../../utils/apiConfig';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {formatMatchDate} from '../../../utils/date/formatMatchDate';
import AdminReferalFilterModal from './AdminReferalFilterModal/AdminReferalFilterModal';

const PAGE_ID = 'AdminUsersPage';

// Подпись реферальной привязки пользователя (см. поле referral в /admin/users)
const REFERRAL_LABELS = {
  friend: 'Реферал друга',
  partner: 'Реферал партнера',
};

// Значения dateSort согласованы с параметром sort сервиса /admin/users;
// "Только игроки" — отдельный переключатель, сочетается с любой из дат
const DATE_SORT_OPTIONS = [
  {id: 'createdAt', label: 'По регистрации'},
  {id: 'lastOnlineAt', label: 'По последнему входу'},
];

function AdminUsersPage({onUserClick, onBack}) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  // Боты в списке админки нужны редко — по умолчанию сервис отдает только
  // живых игроков (см. параметр playersOnly в /admin/users)
  const [playersOnly, setPlayersOnly] = useState(true);
  const [dateSort, setDateSort] = useState('lastOnlineAt');
  // Фильтр по источникам рефералов: пустой массив — фильтр выключен. Список
  // источников выбирается в модалке (см. AdminReferalFilterModal), повторное
  // нажатие на кнопку фильтр сбрасывает
  const [referalSourceIds, setReferalSourceIds] = useState([]);
  // Фильтр «Приложение»: только игроки, получившие награду за первый вход
  // через установленное приложение
  const [appInstalledOnly, setAppInstalledOnly] = useState(false);
  const [isReferalFilterModalOpen, setIsReferalFilterModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isReferalFilterActive = referalSourceIds.length > 0;

  usePageAnalytics('AdminUsersPage');

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const referalQuery = referalSourceIds.length > 0
          ? `&referalSourceIds=${referalSourceIds.join(',')}`
          : '';
        const appInstalledQuery = appInstalledOnly ? '&appInstalledOnly=true' : '';
        const res = await fetch(
          `${API_BASE}/admin/users?playersOnly=${playersOnly}&sort=${dateSort}${referalQuery}${appInstalledQuery}`,
          {credentials: 'include'}
        );
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setUsers(data.users || []);
        setStats(data.stats || null);
      } catch (e) {
        console.error('Fetch users error', e);
        setError('Не удалось загрузить пользователей');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [playersOnly, dateSort, referalSourceIds, appInstalledOnly]);

  return (
    <>
      <PageButton
        className="admin-users-floating-back-button"
        onClick={onBack}
        pageId={PAGE_ID}
        buttonId="back_floating"
      >
        <img
          className="admin-users-floating-back-button_icon"
          src="/img/icons/back_navigation.png"
          alt="Назад"
          title="Назад"
        />
      </PageButton>

      <h1 className="lobby-page-title">Пользователи</h1>

      {stats && (
        <div className="admin-users-stats">
          <div className="admin-users-stats-counts">
            <span className="admin-users-stats-count">
              Игроков: <b>{stats.playerCount}</b>
            </span>
            <span className="admin-users-stats-count">
              Ботов: <b>{stats.botCount}</b>
              <img className="admin-user-item-bot-icon" src="/img/icons/bot.png" alt="Бот"/>
            </span>
          </div>
          <div className="admin-users-stats-grid">
            <span/>
            <span className="admin-users-stats-grid-header">день</span>
            <span className="admin-users-stats-grid-header">неделя</span>
            <span className="admin-users-stats-grid-header">месяц</span>
            <span className="admin-users-stats-grid-label">Регистрации</span>
            <span>{stats.registrations.day}</span>
            <span>{stats.registrations.week}</span>
            <span>{stats.registrations.month}</span>
            <span className="admin-users-stats-grid-label">Входы</span>
            <span>{stats.logins.day}</span>
            <span>{stats.logins.week}</span>
            <span>{stats.logins.month}</span>
          </div>
        </div>
      )}

      <div className="admin-users-sort">
        <PageButton
          className={`admin-users-sort-button${playersOnly ? ' admin-users-sort-button_active' : ''}`}
          pageId={PAGE_ID}
          buttonId="filter_players_only"
          onClick={() => setPlayersOnly((prev) => !prev)}
        >
          Только игроки
        </PageButton>
        {DATE_SORT_OPTIONS.map((option) => (
          <PageButton
            key={option.id}
            className={`admin-users-sort-button${dateSort === option.id ? ' admin-users-sort-button_active' : ''}`}
            pageId={PAGE_ID}
            buttonId={`sort_${option.id}`}
            onClick={() => setDateSort(option.id)}
          >
            {option.label}
          </PageButton>
        ))}
        {/* Выключенная кнопка открывает выбор источников, включенная —
            сбрасывает фильтр */}
        <PageButton
          className={`admin-users-sort-button${appInstalledOnly ? ' admin-users-sort-button_active' : ''}`}
          pageId={PAGE_ID}
          buttonId="filter_app_installed"
          onClick={() => setAppInstalledOnly((prev) => !prev)}
        >
          Приложение
        </PageButton>
        <PageButton
          className={`admin-users-sort-button${isReferalFilterActive ? ' admin-users-sort-button_active' : ''}`}
          pageId={PAGE_ID}
          buttonId="filter_referals"
          onClick={() => {
            if (isReferalFilterActive) {
              setReferalSourceIds([]);
              return;
            }
            setIsReferalFilterModalOpen(true);
          }}
        >
          Фильтр рефералы
          {isReferalFilterActive && ` (${referalSourceIds.length})`}
        </PageButton>
      </div>

      <div className="admin-users-list">
        {loading && <div className="admin-users-status">Загрузка...</div>}
        {error && <div className="admin-users-status">{error}</div>}
        {!loading && !error && users.length === 0 && (
          <div className="admin-users-status">Пользователей нет</div>
        )}
        {!loading &&
          !error &&
          users.map((user) => (
            <PageButton
              key={user.id}
              className="admin-user-item"
              pageId={PAGE_ID}
              buttonId={`user_${user.id}`}
              onClick={() => onUserClick(user.id, user.login)}
            >
              <span className="admin-user-item-name">
                {user.login}
                {user.isBot && (
                  <img
                    className="admin-user-item-bot-icon"
                    src="/img/icons/bot.png"
                    alt="Бот"
                    title="Бот"
                  />
                )}
              </span>
              {user.referral && REFERRAL_LABELS[user.referral.type] && (
                <span className={`admin-user-item-referral`}>
                  {REFERRAL_LABELS[user.referral.type]} <span
                  className={user.referral.type === 'partner' ? 'text-crystals' : 'text-coins'}
                >{user.referral.name}</span>
                </span>
              )}
              {/* Приложение установлено: значок и дата награды за первый вход
                  через него — видно в списке независимо от фильтра */}
              {user.appInstallRewardGrantedAt && (
                <span className="admin-user-item-app-install">
                  <img
                    className="admin-user-item-app-install-icon"
                    src="/img/icons/download.png"
                    alt="Приложение установлено"
                    title="Приложение установлено"
                  />
                  {formatMatchDate(user.appInstallRewardGrantedAt)}
                </span>
              )}
              <span className="admin-user-item-dates">
                <span>Регистрация: {formatMatchDate(user.createdAt)}</span>
                <span>Вход: {formatMatchDate(user.lastOnlineAt)}</span>
              </span>
            </PageButton>
          ))}
      </div>

      <div className="main-menu-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>

      {isReferalFilterModalOpen && (
        <AdminReferalFilterModal
          selectedIds={referalSourceIds}
          onApply={(ids) => {
            setReferalSourceIds(ids);
            setIsReferalFilterModalOpen(false);
          }}
          onClose={() => setIsReferalFilterModalOpen(false)}
        />
      )}
    </>
  );
}

export default AdminUsersPage;
